import type { RouteUpdateArgs } from 'gatsby';
import 'normalize.css';
import 'styles/scss/global.scss';
import { VOGLIO_FIELDS } from '@bondvet/types/voglio';
import { WindowLocation } from '@reach/router';
import { captureGoogleAdsParams } from 'lib/googleAdsTracking';
import {
    captureMetaClickId,
    getMetaBrowserId,
    getMetaClickId,
} from 'lib/metaClickIdTracking';
import client from 'lib/apollo/client';
import { gql } from '@apollo/client';
import { captureUtmParamsFromUrl } from 'analytics/lib/utmTracking';

export { wrapPageElement, wrapRootElement } from './gatsby-shared';

const TRACK_META_PAGE_VIEW_MUTATION = gql`
    mutation TrackMetaPageView($input: MetaPageViewInput!) {
        trackMetaPageView(input: $input) {
            success
            error
        }
    }
`;

function generateMetaEventId(): string {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
        return crypto.randomUUID();
    }

    return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

const META_PAGE_VIEW_MAX_ATTEMPTS = 25;
const META_PAGE_VIEW_RETRY_DELAY_MS = 200;

let isFirstRouteUpdate = true;

function trackMetaPageViewWithRetry(
    location: WindowLocation,
    attempt = 0,
    maxAttempts = META_PAGE_VIEW_MAX_ATTEMPTS,
): void {
    if (typeof window === 'undefined') {
        return;
    }

    const fbq = window.fbq;

    if (typeof fbq !== 'function') {
        if (attempt < maxAttempts) {
            window.setTimeout(
                () =>
                    trackMetaPageViewWithRetry(
                        location,
                        attempt + 1,
                        maxAttempts,
                    ),
                META_PAGE_VIEW_RETRY_DELAY_MS,
            );
        }
        return;
    }

    const eventId = generateMetaEventId();
    const fbc = getMetaClickId();
    const fbp = getMetaBrowserId();
    const url =
        location?.href ??
        (typeof window !== 'undefined' ? window.location.href : undefined);

    try {
        fbq('track', 'PageView', {}, { eventID: eventId });
    } catch (error) {
        console.warn('error tracking Meta PageView via pixel', error);
    }

    client.mutate({
        mutation: TRACK_META_PAGE_VIEW_MUTATION,
        variables: {
            input: {
                eventId,
                fbp: fbp ?? undefined,
                fbc: fbc ?? undefined,
                url,
            },
        },
    });
}

export const onRouteUpdate = ({ location }: RouteUpdateArgs) => {
    const params = new URLSearchParams(location.search);

    // Capture Google Ads tracking parameters (gclid, gbraid, wbraid) from URL
    captureGoogleAdsParams();
    // Capture Meta ClickID (fbclid -> fbc) from URL
    captureMetaClickId();
    const wasFirstRouteUpdate = isFirstRouteUpdate;
    if (isFirstRouteUpdate) {
        isFirstRouteUpdate = false;
    }
    if (
        typeof window !== 'undefined' &&
        window.Cookiebot?.consent?.marketing === true &&
        !wasFirstRouteUpdate
    ) {
        trackMetaPageViewWithRetry(location as WindowLocation);
    }

    // Capture UTM parameters from URL for secondary attribution/debugging
    captureUtmParamsFromUrl();

    // save voglio params to local storage as individual keys
    VOGLIO_FIELDS.forEach((key) => {
        const value = params.get(key);
        if (value) {
            // if we already have something in localStorage, don't overwrite it
            // we want to keep the original value in localStorage
            if (!localStorage.getItem(key)) {
                localStorage.setItem(key, value);
            }
            sessionStorage.setItem(key, value);
        }
    });

    if (window) {
        window.zESettings = {
            webWidget: {
                color: {
                    launcher: '#103559',
                    header: '#10365A',
                    resultLists: '#295889',
                    button: '#103559',
                    articleLinks: '#FF4D4D',
                },
            },
        };
    }

    if (location.pathname.includes('/booking')) {
        // eslint-disable-next-line @typescript-eslint/no-unused-expressions
        window.zE && window.zE('webWidget', 'hide');
    } else {
        // eslint-disable-next-line @typescript-eslint/no-unused-expressions
        window.zE && window.zE('webWidget', 'show');
    }

    const cookiebotWidget = document.getElementById('CookiebotWidget');
    if (cookiebotWidget) {
        if (location.pathname.includes('/admin')) {
            // Hide CookiebotWidget for CMS
            cookiebotWidget.style.display = 'none';
        } else {
            // Ensure CookiebotWidget is visible on other routes
            cookiebotWidget.style.display = '';
        }
    }

    function adjustHeader() {
        // deal with having both the adbar and unbounce scripts
        const adbarHeight = getComputedStyle(document.documentElement)
            .getPropertyValue('--adbar-height')
            .trim();
        const header = document.querySelector(
            '#page-header',
        ) as HTMLElement | null;

        if (header) {
            header.style.top = adbarHeight === '0px' ? 'auto' : adbarHeight;
        }
    }

    adjustHeader();
    window.addEventListener('resize', adjustHeader);

    return () => {
        window.removeEventListener('resize', adjustHeader);
    };
};

function ensureIntersectionObserverPolyfill() {
    const polyFillSrc =
        // eslint-disable-next-line max-len
        'https://cdnjs.cloudflare.com/polyfill/v3/polyfill.min.js?version=4.8.0&features=IntersectionObserver';

    if (!document.querySelector(`script[src="${polyFillSrc}]"`)) {
        const script = document.createElement('script');
        script.setAttribute('defer', 'defer');
        script.setAttribute('src', polyFillSrc);

        document.head.appendChild(script);
    }
}

export function onClientEntry() {
    if (typeof window !== 'undefined') {
        let hasFiredForThisLoad = false;
        const firePageViewIfMarketingConsent = () => {
            if (
                window.Cookiebot?.consent?.marketing === true &&
                !hasFiredForThisLoad
            ) {
                hasFiredForThisLoad = true;
                const locationLike = {
                    href: window.location.href,
                } as WindowLocation;
                trackMetaPageViewWithRetry(locationLike);
            }
        };
        window.addEventListener(
            'CookiebotOnConsentReady',
            firePageViewIfMarketingConsent,
        );
        window.addEventListener(
            'CookiebotOnConsentChange',
            firePageViewIfMarketingConsent,
        );
    }

    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    import('modernizr').then();
    // IntersectionObserver polyfill for gatsby-background-image (Safari, IE)
    if (!('IntersectionObserver' in window)) {
        ensureIntersectionObserverPolyfill();
    }
}

// automatically reload when the service worker has been updated
export const onServiceWorkerUpdateReady = () => {
    window.location.reload();
};

export const shouldUpdateScroll = ({
    routerProps: { location },
}: {
    routerProps: { location: WindowLocation };
}) => {
    const state =
        location && (location.state as { __preserveScroll?: [number, number] });
    // allow passing preserveScroll so we manage the scrolling
    if (state && Array.isArray(state.__preserveScroll)) {
        const [x, y] = state.__preserveScroll;
        window.scrollTo(x, y);
        return false;
    }

    return true;
};
